import { type CSSProperties } from "react";
import { FlowButton } from "@aisquare/ui";
import { ArrowRightIcon, CheckIcon } from "lucide-react";
import { Section, VariantBlock } from "../lib/Section";

/**
 * Pin --primary to the brand navy (+ a light foreground) so the primary flood
 * reads navy in BOTH themes — the lib's shadcn --primary inverts to near-white
 * under .dark, which would wash the pill out. `display:contents` scopes the var
 * without changing VariantBlock's flex layout. (Same pin as Button.demo.)
 */
const NAVY_PRIMARY = {
  "--primary": "212 72% 16%",
  "--primary-foreground": "0 0% 100%",
} as CSSProperties;

const Navy = ({ children }: { children: React.ReactNode }) => (
  <div className="contents" style={NAVY_PRIMARY}>
    {children}
  </div>
);

export function FlowButtonDemo() {
  return (
    <Section
      id="flow-button"
      title="FlowButton"
      description="Pill button whose hover floods in as a circular wash from the corner. As a round signature-animated control it keeps the flood and stays round — no hex notch / travel block / keyline ring — and joins the Onyx press language with just the shared springy bounce + a 1px press-DOWN on :active. Tokenised primary / secondary / ghost. Sits between Button and ShimmerButton."
    >
      <VariantBlock label="Variants">
        <Navy>
          <FlowButton variant="primary">Primary</FlowButton>
        </Navy>
        <FlowButton variant="secondary">Secondary</FlowButton>
        <FlowButton variant="ghost">Ghost</FlowButton>
      </VariantBlock>

      <VariantBlock label="Sizes">
        <Navy>
          <FlowButton size="sm">Small</FlowButton>
          <FlowButton size="md">Medium</FlowButton>
          <FlowButton size="lg">Large</FlowButton>
        </Navy>
      </VariantBlock>

      <VariantBlock label="With icon">
        <Navy>
          <FlowButton icon={<CheckIcon />}>Confirm</FlowButton>
        </Navy>
        <FlowButton variant="secondary" icon={<ArrowRightIcon />}>
          Continue
        </FlowButton>
      </VariantBlock>

      <VariantBlock label="States">
        <Navy>
          <FlowButton disabled>Disabled</FlowButton>
        </Navy>
      </VariantBlock>
    </Section>
  );
}
